import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import KanbanAPI from '../../api/kanban';

export const state = {
  records: [],
  stages: [],
  items: [],
  contactItems: {},
  uiFlags: {
    isFetching: false,
    isFetchingItems: false,
    isFetchingContactItems: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getBoards(_state) {
    return _state.records.sort((a, b) => a.position - b.position);
  },
  getBoard: _state => id => {
    return _state.records.find(record => record.id === Number(id));
  },
  getStages(_state) {
    return [..._state.stages].sort((a, b) => a.position - b.position);
  },
  getItems(_state) {
    return _state.items;
  },
  // Items grouped by stage id, sorted by position within each stage
  getItemsByStage: _state => stageId => {
    return _state.items
      .filter(item => item.kanban_stage_id === stageId)
      .sort((a, b) => a.position - b.position);
  },
  // Kanban memberships for a given contact (across all boards)
  getContactItems: _state => contactId => {
    return _state.contactItems[contactId] || [];
  },
};

export const actions = {
  getBoards: async ({ commit }) => {
    commit(types.SET_KANBAN_UI_FLAG, { isFetching: true });
    try {
      const response = await KanbanAPI.get();
      commit(types.SET_KANBAN_BOARDS, response.data.payload);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetching: false });
    }
  },
  getBoard: async ({ commit }, id) => {
    commit(types.SET_KANBAN_UI_FLAG, { isFetching: true });
    try {
      const response = await KanbanAPI.show(id);
      commit(types.EDIT_KANBAN_BOARD, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetching: false });
    }
  },
  createBoard: async ({ commit }, boardObj) => {
    commit(types.SET_KANBAN_UI_FLAG, { isCreating: true });
    try {
      const response = await KanbanAPI.create({ kanban_board: boardObj });
      commit(types.ADD_KANBAN_BOARD, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isCreating: false });
    }
  },
  updateBoard: async ({ commit }, { id, ...boardObj }) => {
    commit(types.SET_KANBAN_UI_FLAG, { isUpdating: true });
    try {
      const response = await KanbanAPI.update(id, { kanban_board: boardObj });
      commit(types.EDIT_KANBAN_BOARD, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isUpdating: false });
    }
  },
  deleteBoard: async ({ commit }, id) => {
    commit(types.SET_KANBAN_UI_FLAG, { isDeleting: true });
    try {
      await KanbanAPI.delete(id);
      commit(types.DELETE_KANBAN_BOARD, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isDeleting: false });
    }
  },

  // Stages
  getStages: async ({ commit }, boardId) => {
    commit(types.SET_KANBAN_UI_FLAG, { isFetching: true });
    try {
      const response = await KanbanAPI.getStages(boardId);
      commit(types.SET_KANBAN_STAGES, response.data.payload);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetching: false });
    }
  },
  createStage: async ({ commit, state: _state }, { boardId, ...stageObj }) => {
    commit(types.SET_KANBAN_UI_FLAG, { isCreating: true });
    try {
      const response = await KanbanAPI.createStage(boardId, stageObj);
      commit(types.SET_KANBAN_STAGES, [..._state.stages, response.data]);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isCreating: false });
    }
  },
  updateStage: async (
    { commit, state: _state },
    { boardId, id, ...stageObj }
  ) => {
    commit(types.SET_KANBAN_UI_FLAG, { isUpdating: true });
    try {
      const response = await KanbanAPI.updateStage(boardId, id, stageObj);
      const updated = _state.stages.map(stage =>
        stage.id === response.data.id ? response.data : stage
      );
      commit(types.SET_KANBAN_STAGES, updated);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isUpdating: false });
    }
  },
  deleteStage: async ({ commit, state: _state }, { boardId, id }) => {
    commit(types.SET_KANBAN_UI_FLAG, { isDeleting: true });
    try {
      await KanbanAPI.deleteStage(boardId, id);
      commit(
        types.SET_KANBAN_STAGES,
        _state.stages.filter(stage => stage.id !== id)
      );
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isDeleting: false });
    }
  },
  reorderStages: async ({ commit }, { boardId, stageIds }) => {
    try {
      const response = await KanbanAPI.reorderStages(boardId, stageIds);
      commit(types.SET_KANBAN_STAGES, response.data.payload);
    } catch (error) {
      throw new Error(error);
    }
  },

  // Items
  getItems: async ({ commit }, boardId) => {
    commit(types.SET_KANBAN_UI_FLAG, { isFetchingItems: true });
    try {
      const response = await KanbanAPI.getItems(boardId);
      commit(types.SET_KANBAN_ITEMS, response.data.payload);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetchingItems: false });
    }
  },
  addItem: async ({ commit }, { boardId, ...itemObj }) => {
    commit(types.SET_KANBAN_UI_FLAG, { isCreating: true });
    try {
      const response = await KanbanAPI.addItem(boardId, itemObj);
      commit(types.SET_KANBAN_ITEM, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isCreating: false });
    }
  },
  // Optimistic update is performed in the component; this persists the move.
  moveItem: async ({ commit }, { boardId, id, kanbanStageId, position }) => {
    const response = await KanbanAPI.moveItem(boardId, id, {
      kanbanStageId,
      position,
    });
    commit(types.SET_KANBAN_ITEM, response.data);
    return response.data;
  },
  // Replaces the whole items list (used to revert an optimistic update).
  setItems: ({ commit }, items) => {
    commit(types.SET_KANBAN_ITEMS, items);
  },
  deleteItem: async ({ commit }, { boardId, id }) => {
    commit(types.SET_KANBAN_UI_FLAG, { isDeleting: true });
    try {
      await KanbanAPI.deleteItem(boardId, id);
      commit(types.DELETE_KANBAN_ITEM, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isDeleting: false });
    }
  },
  // Fetch the boards/stages a contact currently belongs to.
  getContactItems: async ({ commit }, contactId) => {
    commit(types.SET_KANBAN_UI_FLAG, { isFetchingContactItems: true });
    try {
      const response = await KanbanAPI.getContactItems(contactId);
      commit(types.SET_KANBAN_CONTACT_ITEMS, {
        contactId,
        items: response.data.payload,
      });
      return response.data.payload;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetchingContactItems: false });
    }
  },
};

export const mutations = {
  [types.SET_KANBAN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  [types.SET_KANBAN_BOARDS]: MutationHelpers.set,
  [types.ADD_KANBAN_BOARD]: MutationHelpers.create,
  [types.EDIT_KANBAN_BOARD]: MutationHelpers.setSingleRecord,
  [types.DELETE_KANBAN_BOARD]: MutationHelpers.destroy,

  [types.SET_KANBAN_STAGES](_state, data) {
    _state.stages = data;
  },
  [types.SET_KANBAN_ITEMS](_state, data) {
    _state.items = data;
  },
  [types.SET_KANBAN_ITEM](_state, data) {
    const index = _state.items.findIndex(item => item.id === data.id);
    if (index > -1) {
      _state.items[index] = data;
    } else {
      _state.items.push(data);
    }
  },
  [types.DELETE_KANBAN_ITEM](_state, id) {
    _state.items = _state.items.filter(item => item.id !== id);
  },
  [types.SET_KANBAN_CONTACT_ITEMS](_state, { contactId, items }) {
    _state.contactItems = {
      ..._state.contactItems,
      [contactId]: items,
    };
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
